import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Link } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import { useT } from "@/lib/translations";
import { useLanguage } from "@/context/LanguageContext";
import { parsePresetList, parsePrice } from "@/lib/pricingHelpers";
import { trackFunnelStep } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LocalePhoneInput from "@/components/LocalePhoneInput";
import { useValidators } from "@/lib/contactValidators";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ChevronLeft, ChevronRight, CreditCard, Building2, Copy, CheckCircle,
  Send, Info, Heart, Utensils, Stethoscope, HandHeart, PawPrint
} from "lucide-react";

const DEFAULT_IBAN = "TR36 0001 5001 5800 7319 4625 42";
const DEFAULT_DERNEK_ADI = "Pati Bekçileri Yaşatma ve Koruma Derneği";

const MAMA_DAYS = [
  { days: 1 },
  { days: 3 },
  { days: 7, recommended: true },
  { days: 14 },
  { days: 30 },
];

// Hardcoded fallbacks — CMS values from `pricing.*` keys override these at runtime.
const FALLBACK_GENERAL_PRESETS = [250, 500, 1000, 2500];
const FALLBACK_MONTHLY_PRESETS = [500, 1000, 2000, 3500];
const FALLBACK_VET_VISITS = [1, 2, 3, 5];

const formatTL = (n, locale = "tr") => `${Number(n).toLocaleString(locale === "en" ? "en-US" : "tr-TR")}${locale === "en" ? " TL" : "₺"}`;

// type: "mama" | "vet" | "genel"
// presetAmount: number — if set, skips step 1 (amount selection) and uses this fixed amount
// presetDonationType: "meal" | "vet_visit" | "general" | "monthly_general" — overrides donation_type sent to backend
// presetTitle: string — title shown in place of dog-specific text (e.g. "Genel Mama Desteği")
// monthlyMode: bool — if true, frame copy as "monthly support" and skip impactful "tek seferlik" phrasing
export default function DonationFlow({ open, onClose, dogId, dogName, type, presetAmount, presetDonationType, presetTitle, monthlyMode, upsellVariant = "guardian", onUpsellMeal }) {
  const { t } = useContent();
  const L = useT();
  const { locale } = useLanguage();
  const { getPhoneError, getEmailError } = useValidators();
  const IBAN = t("donation.iban", DEFAULT_IBAN);
  const DERNEK_ADI = t("donation.dernek_adi", DEFAULT_DERNEK_ADI);
  // Prices — CMS keys take priority, then legacy keys, then hardcoded fallbacks.
  const MAMA_PRICE_PER_DAY = parsePrice(t("pricing.mama_price_per_day", t("donation.mama_price", "90")), 90);
  const VET_PRICE_PER_VISIT = parsePrice(t("pricing.vet_price_per_visit", t("donation.vet_price", "1900")), 1900);
  const GENERAL_MIN = parsePrice(t("pricing.general_min", t("donation.min_amount", "100")), 100);
  const MONTHLY_MIN = parsePrice(t("pricing.monthly_min", "500"), 500);

  // Build VET_VISITS option array from CMS list with default-marker.
  const vetVisitsValues = parsePresetList(t("pricing.vet_visits_options", ""), FALLBACK_VET_VISITS);
  const vetVisitsDefault = parsePrice(t("pricing.vet_visits_default", "2"), 2);
  const VET_VISITS = vetVisitsValues.map((v) => ({
    visits: v,
    recommended: v === vetVisitsDefault,
  }));

  // General / Monthly presets from CMS (comma-separated numbers).
  const generalPresetValues = parsePresetList(t("pricing.general_presets", ""), FALLBACK_GENERAL_PRESETS);
  const generalDefault = parsePrice(t("pricing.general_default", "500"), 500);
  const GENERAL_PRESETS = generalPresetValues.map((v) => ({
    value: v,
    label: formatTL(v, locale),
    recommended: v === generalDefault,
  }));

  const monthlyPresetValues = parsePresetList(t("pricing.monthly_presets", ""), FALLBACK_MONTHLY_PRESETS);
  const monthlyDefault = parsePrice(t("pricing.monthly_default", "2000"), 2000);
  const MONTHLY_PRESETS = monthlyPresetValues.map((v) => ({
    value: v,
    label: formatTL(v, locale),
    recommended: v === monthlyDefault,
  }));

  // Vet quick-amount chips (Hızlı katkı seç) — CMS-tunable.
  const vetQuickAmounts = parsePresetList(t("pricing.vet_quick_amounts", ""), [250, 500, 1000]);
  const vetQuickDefault = parsePrice(t("pricing.vet_quick_default", "500"), 500);

  // If preset amount provided, start directly at the contact step (skip amount picker).
  const initialStep = presetAmount ? 2 : 1;
  const [step, setStep] = useState(initialStep);
  const [donationId, setDonationId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [cardUnavailable, setCardUnavailable] = useState(false);

  // mama
  const [mamaDays, setMamaDays] = useState(7);
  // vet
  const [vetVisits, setVetVisits] = useState(vetVisitsDefault);
  const [vetQuickAmount, setVetQuickAmount] = useState(null);
  // genel — start empty so donor picks their own amount (no preset commitment).
  // `presetAmount` (from e-mail deep links or DogProfile sidebar) still honored.
  const initGenel = presetAmount || 0;
  const [genelAmount, setGenelAmount] = useState(initGenel);
  const [genelCustom, setGenelCustom] = useState(initGenel ? String(initGenel) : "");
  const [genelPreset, setGenelPreset] = useState(initGenel || null);

  // Active preset list + minimum for current mode
  const activePresets = monthlyMode ? MONTHLY_PRESETS : GENERAL_PRESETS;
  const activeMin = monthlyMode ? MONTHLY_MIN : GENERAL_MIN;

  // contact
  const [adSoyad, setAdSoyad] = useState("");
  const [telefon, setTelefon] = useState("");
  const [eposta, setEposta] = useState("");

  // payment
  const [paymentMethod, setPaymentMethod] = useState("");

  // Fonzip integration — when monthlyMode AND fonzip.monthly_enabled=true,
  // route the iyzico-style "kart ile öde" step to Fonzip's hosted recurring page.
  const [fonzipMonthlyEnabled, setFonzipMonthlyEnabled] = useState(false);
  const [fonzipOneoffEnabled, setFonzipOneoffEnabled] = useState(false);
  const [fonzipHandoff, setFonzipHandoff] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/fonzip/status`).then((r) => {
      if (cancelled) return;
      setFonzipMonthlyEnabled(!!r.data?.monthly_enabled);
      setFonzipOneoffEnabled(!!r.data?.oneoff_enabled);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Funnel: donation flow opened (Meta InitiateCheckout equivalent).
  // Fire only when `open` transitions to true to avoid re-count on every render.
  useEffect(() => {
    if (!open) return;
    trackFunnelStep("begin_donation", {
      donation_type: presetDonationType || type,
      monthly: !!monthlyMode,
      dog_id: dogId || null,
    });
  }, [open, type, presetDonationType, monthlyMode, dogId]);

  // bank verify
  const [showVerify, setShowVerify] = useState(false);
  const [bankName, setBankName] = useState("");
  const [transferDate, setTransferDate] = useState(new Date().toISOString().split("T")[0]);
  const [transferAmount, setTransferAmount] = useState("");

  const getTotal = () => {
    if (presetAmount) return presetAmount;
    // All donation types (mama/vet/genel) now use free-entered amount — no preset multipliers.
    return genelAmount;
  };

  const getQuantityLabel = () => {
    if (presetTitle) return L(presetTitle);
    if (type === "mama") return L("Mama Desteği");
    if (type === "vet") return L("Veteriner Desteği");
    return L("Genel bağış");
  };

  const getImpactText = () => {
    // Deliberately vague — we cannot make specific quantity claims (legal liability).
    if (type === "mama") return L("Bağışın mama bütçesine katkı olacak.");
    if (type === "vet") return L("Bağışın veteriner bütçesine katkı olacak.");
    return L("Bağışın can dostlarımızın bakımına gider.");
  };

  const getThankYouText = () => {
    if (presetTitle) return L("{title} desteğiniz alındı. Teşekkürler!").replace("{title}", L(presetTitle));
    if (type === "mama") return L("Mama desteğin alındı. Teşekkürler!");
    if (type === "vet") return L("Veteriner desteğin alındı. Teşekkürler!");
    return L("Bağışın alındı. Teşekkürler!");
  };

  const aciklamaKodu = (() => {
    const name = (presetTitle || dogName || "KOPEK").toUpperCase().replace(/[^A-Z0-9]/g, "");
    const tel4 = telefon.replace(/\D/g, "").slice(-4) || "0000";
    const tipMap = { mama: "MAMA", vet: "VET", genel: "GENEL" };
    return `KAHU-BAGIS-${name}-${tel4}-${tipMap[type] || "GENEL"}`;
  })();

  const canProceedStep1 = () => {
    // Free-amount: all types now require a valid entered amount ≥ activeMin
    return genelAmount >= activeMin;
  };

  const phoneError = getPhoneError(telefon, { required: true });
  const emailError = getEmailError(eposta, { required: false });

  const canProceedStep2 = () =>
    adSoyad.trim() && !phoneError && telefon.trim() && !emailError;

  const handleSelectGenelPreset = (val) => {
    setGenelPreset(val);
    setGenelAmount(val);
    setGenelCustom(String(val));
  };

  const handleGenelCustom = (val) => {
    const num = parseInt(val) || 0;
    setGenelCustom(val);
    setGenelAmount(num);
    setGenelPreset(activePresets.find(p => p.value === num)?.value || null);
  };

  const handleCreateDonation = async (methodOverride = null) => {
    setSubmitting(true);
    const method = methodOverride || paymentMethod;
    if (methodOverride && methodOverride !== paymentMethod) setPaymentMethod(methodOverride);
    try {
      const total = getTotal();
      let endpoint, payload;
      const emailFields = eposta ? { donor_email: eposta } : {};

      // Preset mode (ana sayfadaki genel destek butonları / e-posta derin linkleri):
      // tutar kullanıcıdan gelir, presetAmount yalnızca ön-doldurma içindir.
      // Can profiline bağlı olmayan bağışlar /donations'a gider (micro-donations dog_id zorunlu).
      if (presetDonationType) {
        endpoint = "/donations";
        payload = {
          dog_id: null,
          donor_name: adSoyad,
          donor_phone: telefon,
          ...emailFields,
          amount: total,
          donation_type: presetDonationType,
          payment_method: method,
          quantity_label: getQuantityLabel(),
        };
      } else if (type === "vet" && vetQuickAmount) {
        endpoint = "/donations";
        payload = {
          dog_id: dogId, donor_name: adSoyad, donor_phone: telefon, ...emailFields,
          amount: total, donation_type: "vet_visit", payment_method: method,
          quantity_label: getQuantityLabel(),
        };
      } else if (type === "genel") {
        endpoint = "/donations";
        payload = {
          dog_id: dogId, donor_name: adSoyad, donor_phone: telefon, ...emailFields,
          amount: total,
          donation_type: monthlyMode ? "monthly_general" : "general",
          payment_method: method,
          quantity_label: monthlyMode ? `Aylık ${total}₺ Genel Destek` : getQuantityLabel(),
          message: monthlyMode ? "Aylık genel destek talebi" : undefined,
        };
      } else if (!dogId) {
        // Can seçilmemişse mikro bağış ucu kullanılamaz (dog_id zorunlu) → genel bağış olarak kaydet
        endpoint = "/donations";
        payload = {
          dog_id: null, donor_name: adSoyad, donor_phone: telefon, ...emailFields,
          amount: total,
          donation_type: type === "mama" ? "meal" : "vet_visit",
          payment_method: method,
          quantity_label: getQuantityLabel(),
        };
      } else {
        endpoint = "/micro-donations";
        payload = {
          dog_id: dogId, donor_name: adSoyad, donor_phone: telefon, ...emailFields,
          donation_type: type === "mama" ? "meal" : "vet_visit",
          // quantity artık kullanılmıyor — kullanıcı doğrudan tutarı belirliyor.
          // Backend impact metrics'i (varsa) sadece analytics için toplam üzerinden hesaplayabilir.
          amount: total,
          payment_method: method,
        };
      }
      const res = await axios.post(`${API}${endpoint}`, payload);
      setDonationId(res.data.donation_id);
      setTransferAmount(String(total));
      setStep(4);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      console.error("donation create failed", status, detail || error.message);
      toast.error(
        typeof detail === "string" && detail
          ? detail
          : `${L("İşlem başarısız")}${status ? ` (${status})` : ""}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyPayment = async () => {
    if (!donationId) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/donations/verify`, {
        donation_id: donationId,
        bank_name: bankName || null,
        transfer_date: transferDate,
        amount: parseFloat(transferAmount) || getTotal(),
      });
      setStep(5);
    } catch (error) {
      toast.error(L("Ödeme bildirimi gönderilemedi"));
    } finally {
      setSubmitting(false);
    }
  };

  // Düzenli bağış Fonzip'te tahsil edildiğinde IBAN ile aylık talimat verilemiyor →
  // ödeme yöntemi adımını tamamen atlayıp doğrudan Fonzip devrine geçiyoruz.
  // Kart ile ödeme Fonzip üzerinden mi yürüyecek? (aylık → Fonzip, tek seferlik → Fonzip)
  const monthlyViaFonzip = !!monthlyMode && fonzipMonthlyEnabled;
  const cardViaFonzip = monthlyMode ? fonzipMonthlyEnabled : fonzipOneoffEnabled;
  const skipPaymentStep = monthlyViaFonzip;

  const handleNext = () => {
    if (step === 1) setStep(2);
    else if (step === 2) {
      if (skipPaymentStep) handleCreateDonation("iyzico");
      else setStep(3);
    }
    else if (step === 3) handleCreateDonation();
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(L("{label} kopyalandı!").replace("{label}", label));
  };

  const resetFlowState = () => {
    setStep(initialStep);
    setDonationId(null);
    setShowVerify(false);
    setPaymentMethod("");
    setVetQuickAmount(null);
    setVetVisits(2);
    setEposta("");
    setGenelPreset(null);
    setGenelCustom("");
  };

  const handleClose = () => {
    resetFlowState();
    onClose();
  };

  const total = getTotal();
  const TypeIcon = type === "mama" ? Utensils : type === "vet" ? Stethoscope : HandHeart;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid={`donation-dialog-${type}`}>
        <DialogTitle className="sr-only">
          {type === "mama" ? L("Mama Desteği") : type === "vet" ? L("Veteriner Desteği") : L("Genel Bağış")}
        </DialogTitle>
        {/* Progress bar */}
        <div className="h-1.5 bg-muted">
          <div className="h-full bg-primary transition-all" style={{ width: `${(step / (skipPaymentStep ? 4 : 5)) * 100}%` }} />
        </div>

        <div className="p-6 space-y-5">
          {/* Step 1: Amount Selection */}
          {step === 1 && (
            <div className="space-y-5" data-testid="donation-step1">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <TypeIcon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="font-heading text-xl">
                    {monthlyMode ? L("Aylık Destek Ol") :
                     type === "mama" ? L("Mama Desteği") : type === "vet" ? L("Veteriner Desteği") : L("Genel Bağış")}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {monthlyMode
                      ? L("Her ay düzenli desteğinle yüzlerce can güvende kalsın.")
                      : presetTitle
                        ? (type === "mama"
                            ? L("Bakımını üstlendiğimiz canların mama masraflarına katkıda bulun.")
                            : type === "vet"
                              ? L("Bakımını üstlendiğimiz canların veteriner bakımına destek ol.")
                              : L("Bakımını üstlendiğimiz canlara tek seferlik destekle umut ol."))
                        : (type === "mama"
                            ? L("{name} ve arkadaşları için mama desteği ver.").replace("{name}", dogName)
                            : type === "vet"
                              ? L("{name}'nın veteriner bakımına katkıda bulun.").replace("{name}", dogName)
                              : L("Tek seferlik destekle {name}'nın bakımına katkıda bulun.").replace("{name}", dogName))}
                  </p>
                </div>
              </div>

              {/* Free-amount input for ALL donation types (mama/vet/genel).
                  No preset amounts, no unit displays (days/visits), no impact estimation —
                  legally we cannot suggest specific quantities since real costs vary. */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">
                  {monthlyMode ? L("Aylık Bağış Tutarı") : L("Bağış Tutarı")}
                  <span className="text-destructive ml-1">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-muted-foreground">
                    {locale === "en" ? "TL" : "₺"}
                  </span>
                  <Input
                    type="number"
                    value={genelCustom}
                    onChange={(e) => handleGenelCustom(e.target.value)}
                    className="pl-12 h-14 text-xl font-heading font-semibold"
                    min={activeMin}
                    placeholder={locale === "en" ? "Enter amount" : "Tutar giriniz"}
                    data-testid="donation-amount-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground italic">
                  {L("Bağış tutarını sen belirlersin. Her katkı — büyüklüğü ne olursa olsun — bir cana dokunur.")}
                </p>
                {genelAmount > 0 && genelAmount < activeMin && (
                  <Alert className="border-destructive/50 bg-destructive/10">
                    <AlertDescription className="text-destructive text-sm">
                      {L("Minimum tutar {n}").replace("{n}", `${activeMin}${locale === "en" ? " TL" : "₺"}${monthlyMode ? (locale === "en" ? "/mo" : "/ay") : ""}`) + (locale === "en" ? "" : "'dir.")}
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <Button onClick={() => setStep(2)} disabled={!canProceedStep1()} className="w-full btn-pill bg-primary text-primary-foreground h-11" data-testid="donation-step1-next">
                {monthlyMode
                  ? L("{amount} ile Aylık Destek Ol").replace("{amount}", `${total.toLocaleString(locale === "en" ? "en-US" : "tr-TR")}${locale === "en" ? " TL" : "₺"}`)
                  : total > 0
                    ? (locale === "en"
                        ? `${L("Bağış Yap")} — ${Number(total).toLocaleString("en-US")} TL`
                        : `${Number(total).toLocaleString("tr-TR")}₺ ${L("Bağış Yap")}`)
                    : L("Devam et")}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Contact */}
          {step === 2 && (
            <div className="space-y-5" data-testid="donation-step2">
              {presetTitle && (
                <div className="flex items-center gap-3 pb-2 border-b border-border/50">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <TypeIcon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl">{L(presetTitle)}</h2>
                    <p className="text-sm text-primary font-medium">{total.toLocaleString(locale === "en" ? "en-US" : "tr-TR")}{locale === "en" ? " TL" : "₺"}</p>
                  </div>
                </div>
              )}
              <div>
                <h2 className="font-heading text-xl">{L("İletişim Bilgileri")}</h2>
                <p className="text-sm text-muted-foreground">{L("Ödeme eşleştirmesi için kısa bilgi.")}</p>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>{L("Ad Soyad")} <span className="text-destructive">*</span></Label>
                  <Input value={adSoyad} onChange={(e) => setAdSoyad(e.target.value)} placeholder={L("Adınız Soyadınız")} className="mt-1.5" data-testid="donation-ad-soyad" />
                </div>
                <div>
                  <Label>{L("Telefon")} <span className="text-destructive">*</span></Label>
                  <div className="mt-1.5">
                    <LocalePhoneInput
                      value={telefon}
                      onChange={(v) => setTelefon(v)}
                      testid="donation-telefon"
                      invalid={!!(telefon && phoneError)}
                    />
                  </div>
                  {telefon && phoneError && (
                    <p className="text-xs text-destructive mt-1.5" data-testid="donation-telefon-error">{phoneError}</p>
                  )}
                </div>
                <div>
                  <Label>{L("E-posta")} <span className="text-xs text-muted-foreground">{L("(makbuz/teşekkür için)")}</span></Label>
                  <Input type="email" value={eposta} onChange={(e) => setEposta(e.target.value)} placeholder="ornek@email.com" className="mt-1.5" data-testid="donation-eposta" />
                  {eposta && emailError && (
                    <p className="text-xs text-destructive mt-1.5" data-testid="donation-eposta-error">{emailError}</p>
                  )}
                </div>
              </div>
              <div className="p-3 bg-muted/30 rounded-xl text-center">
                <p className="text-sm font-medium">{L("Toplam:")} {total.toLocaleString(locale === "en" ? "en-US" : "tr-TR")}{locale === "en" ? " TL" : "₺"}</p>
                <p className="text-xs text-primary">{getImpactText()}</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => (presetAmount ? handleClose() : setStep(1))} className="btn-pill" data-testid="donation-step2-back">
                  <ChevronLeft className="w-4 h-4 mr-1" /> {presetAmount ? L("Vazgeç") : L("Geri")}
                </Button>
                <Button onClick={() => (skipPaymentStep ? handleCreateDonation("iyzico") : setStep(3))} disabled={!canProceedStep2() || submitting} className="flex-1 btn-pill bg-primary text-primary-foreground h-11" data-testid="donation-step2-next">
                  {submitting ? L("Hazırlanıyor...") : (skipPaymentStep ? L("Düzenli Desteğe Devam Et") : L("Devam et"))} <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Payment Method */}
          {step === 3 && (
            <div className="space-y-5" data-testid="donation-step3">
              <div>
                <h2 className="font-heading text-xl">{L("Ödeme Yöntemi Seç")}</h2>
                <p className="text-sm text-muted-foreground">{L("Bağışını hızlıca tamamla.")}</p>
              </div>
              <div className="space-y-3">
                <button type="button" onClick={() => setPaymentMethod("iyzico")}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${paymentMethod === "iyzico" ? "border-primary bg-primary/10" : "border-muted hover:bg-muted/30"}`}
                  data-testid="donation-pay-iyzico">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <CreditCard className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{monthlyViaFonzip ? L("Kart ile Düzenli Öde (Fonzip)") : cardViaFonzip ? L("Kart ile Öde (Fonzip)") : L("Kart ile Öde")}</p>
                    <p className="text-xs text-muted-foreground">
                      {cardUnavailable
                        ? L("Şu anda kullanılamıyor — banka havalesi/EFT ile devam edebilirsin.")
                        : monthlyViaFonzip ? L("Aylık talimatını Fonzip'in güvenli sayfasında ver.") : cardViaFonzip ? L("Fonzip'in güvenli bağış sayfasında kartla öde.") : L("Hızlı, kolay ve anında onay.")}
                    </p>
                  </div>
                </button>
                <button type="button" onClick={() => setPaymentMethod("bank_transfer")}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${paymentMethod === "bank_transfer" ? "border-primary bg-primary/10" : "border-muted hover:bg-muted/30"}`}
                  data-testid="donation-pay-bank">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{L("Banka Havalesi / EFT (IBAN)")}</p>
                    <p className="text-xs text-muted-foreground">{L("Banka uygulamandan transfer yapabilirsin.")}</p>
                  </div>
                </button>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)} className="btn-pill" data-testid="donation-step3-back">
                  <ChevronLeft className="w-4 h-4 mr-1" /> {L("Geri")}
                </Button>
                <Button onClick={handleNext} disabled={!paymentMethod || submitting} className="flex-1 btn-pill bg-primary text-primary-foreground h-11" data-testid="donation-step3-next">
                  {submitting ? L("İşleniyor...") : L("Devam et")} <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Payment */}
          {step === 4 && paymentMethod === "bank_transfer" && (
            <div className="space-y-5" data-testid="donation-step4-bank">
              <div>
                <h2 className="font-heading text-xl">{L("Banka Havalesi / EFT ile Bağış")}</h2>
              </div>
              <div className="bg-muted/30 rounded-xl p-4 space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">{L("Dernek adı ve hesap ismi")}</Label>
                  <p className="font-medium text-sm mt-1">{DERNEK_ADI}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">IBAN</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="font-mono font-medium text-sm">{IBAN}</p>
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(IBAN.replace(/\s/g, ""), L("IBAN"))} data-testid="donation-copy-iban">
                      <Copy className="w-3.5 h-3.5 mr-1" /> {L("Kopyala")}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">{L("Açıklama kodu")}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="font-mono font-medium text-sm">{aciklamaKodu}</p>
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(aciklamaKodu, L("Açıklama kodu"))} data-testid="donation-copy-code">
                      <Copy className="w-3.5 h-3.5 mr-1" /> {L("Kopyala")}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{L("Lütfen havale açıklamasına bu kodu yazın. Böylece bağışınızı hızlıca eşleştiririz.")}</p>
                </div>
                <div className="p-3 bg-primary/5 rounded-lg text-center">
                  <p className="text-sm font-medium">{L("Ödenecek tutar:")} {Number(total).toLocaleString(locale === "en" ? "en-US" : "tr-TR")}{locale === "en" ? " TL" : "₺"}</p>
                </div>
              </div>

              {!showVerify ? (
                <div className="space-y-2">
                  <Button className="w-full btn-pill bg-primary text-primary-foreground h-11" onClick={() => setShowVerify(true)} data-testid="donation-payment-done">
                    <CheckCircle className="w-5 h-5 mr-2" /> {L("Ödeme Yaptım")}
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full btn-pill text-muted-foreground"
                    onClick={() => { setPaymentMethod(""); setStep(3); }}
                    data-testid="donation-bank-back"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" /> {L("Ödeme Yöntemini Değiştir")}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 p-4 bg-muted/20 rounded-xl border border-border/50" data-testid="donation-verify-form">
                  <h3 className="font-medium text-sm">{L("Ödeme Doğrulama")}</h3>
                  <div>
                    <Label className="text-xs">{L("Tarih")} <span className="text-destructive">*</span></Label>
                    <Input type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} className="mt-1" data-testid="donation-verify-date" />
                  </div>
                  <div>
                    <Label className="text-xs">{L("Tutar")}{locale === "en" ? " (TL)" : " (₺)"}</Label>
                    <Input type="number" value={transferAmount} onChange={(e) => setTransferAmount(e.target.value)} className="mt-1" data-testid="donation-verify-amount" />
                  </div>
                  <Button className="w-full btn-pill bg-primary text-primary-foreground" onClick={handleVerifyPayment} disabled={submitting || !transferDate} data-testid="donation-verify-submit">
                    {submitting ? L("Gönderiliyor...") : L("Gönder")} <Send className="w-4 h-4 ml-2" />
                  </Button>
                  <Button variant="ghost" className="w-full btn-pill text-muted-foreground" onClick={() => setShowVerify(false)} data-testid="donation-verify-back">
                    <ChevronLeft className="w-4 h-4 mr-2" /> {L("Geri")}
                  </Button>
                </div>
              )}
            </div>
          )}

          {step === 4 && paymentMethod === "iyzico" && fonzipHandoff && (
            <div className="space-y-4 text-center" data-testid="donation-fonzip-handoff">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto">
                <CheckCircle className="w-7 h-7 text-primary" />
              </div>
              <h2 className="font-heading text-xl">{L("Fonzip ödeme sayfası yeni sekmede açıldı")}</h2>
              <p className="text-sm text-muted-foreground">
                {monthlyMode
                  ? L("Fonzip'te \"Aylık\" seçeneğini işaretle, tutarını ve kart bilgilerini gir. Eşleştirme için aynı e-postayı kullan:")
                  : L("Fonzip sayfasında tutarını ve kart bilgilerini gir (tek seferlik). Eşleştirme için aynı e-postayı kullan:")} <strong>{eposta || "—"}</strong>
              </p>
              <button
                type="button"
                className="font-mono text-sm font-bold px-3 py-1.5 rounded bg-muted border inline-flex items-center gap-1"
                onClick={() => { navigator.clipboard.writeText(fonzipHandoff.ref_code || "").then(() => toast.success(L("Kopyalandı"))).catch(() => {}); }}
                data-testid="donation-fonzip-ref-code"
              >
                {L("KAHU Referans Kodun")}: {fonzipHandoff.ref_code} <Copy className="w-3 h-3" />
              </button>
              {(
                <a href={fonzipHandoff.url} target="_blank" rel="noreferrer noopener" className="block text-sm text-primary underline" data-testid="donation-fonzip-open-link">
                  {L("Sayfa açılmadıysa buraya tıkla →")}
                </a>
              )}
              <Button className="w-full btn-pill bg-primary text-primary-foreground h-12" onClick={() => setStep(5)} data-testid="donation-fonzip-done">
                {L("Ödemeyi Tamamladım")}
              </Button>
              <Button variant="outline" className="btn-pill" onClick={() => window.open(fonzipHandoff.url, "_blank", "noopener")} data-testid="donation-fonzip-reopen">
                {L("Fonzip sayfasını yeniden aç")}
              </Button>
            </div>
          )}

          {step === 4 && paymentMethod === "iyzico" && !fonzipHandoff && (
            <div className="space-y-5 text-center" data-testid="donation-step4-iyzico">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto">
                <CreditCard className="w-7 h-7 text-primary" />
              </div>
              <h2 className="font-heading text-xl">{monthlyViaFonzip ? L("Düzenli Ödeme (Fonzip)") : cardViaFonzip ? L("Kart ile Öde (Fonzip)") : L("Kart ile Öde")}</h2>
              <p className="text-muted-foreground">
                {monthlyViaFonzip ? L("Aylık tutar:") + " " : L("Ödenecek tutar:") + " "}<strong>{Number(total).toLocaleString(locale === "en" ? "en-US" : "tr-TR")}{locale === "en" ? " TL" : "₺"}{monthlyViaFonzip ? (locale === "en" ? "/mo" : "/ay") : ""}</strong>
              </p>
              <p className="text-sm text-muted-foreground">
                {monthlyViaFonzip
                  ? L("Fonzip'in güvenli sayfasında kart bilgilerinizi tanımlayacaksınız. Her ay otomatik tahsil edilir, dilediğiniz zaman iptal edebilirsiniz.")
                  : cardViaFonzip
                    ? L("Fonzip'in güvenli bağış sayfasına yönlendirileceksiniz. Kart bilgileriniz bizde saklanmaz.")
                    : L("Güvenli ödeme sayfasına yönlendirileceksiniz. Kart bilgileriniz bizde saklanmaz.")}
              </p>
              <Button 
                className="w-full btn-pill bg-primary text-primary-foreground h-12" 
                onClick={async () => {
                  // Mobil Safari pop-up engellemesini aşmak için sekmeyi tıklama anında aç,
                  // adresi istek dönünce ata.
                  const win = window.open("", "_blank");
                  if (win) {
                    try {
                      win.document.write(`<!doctype html><meta name="viewport" content="width=device-width,initial-scale=1"><title>Fonzip</title><body style="font-family:system-ui;padding:24px;text-align:center;color:#444">${L("Ödeme sayfası açılıyor...")}</body>`);
                    } catch (e) { /* noop */ }
                  }
                  setSubmitting(true);
                  try {
                    const endpoint = monthlyViaFonzip
                      ? "/fonzip/monthly/initiate"
                      : fonzipOneoffEnabled
                        ? "/fonzip/oneoff/initiate"
                        : "/iyzico/checkout/initialize";
                    const res = await axios.post(`${API}${endpoint}`, {
                      donation_id: donationId,
                      amount: total,
                      donor_name: adSoyad,
                      donor_email: eposta || `noreply+${(telefon || "anon").replace(/\D/g, "")}@kahu.com.tr`,
                      donor_phone: telefon || "+905000000000"
                    });
                    if (res.data.paymentPageUrl && cardViaFonzip) {
                      if (win) {
                        win.location.href = res.data.paymentPageUrl;
                      } else {
                        window.open(res.data.paymentPageUrl, "_blank", "noopener");
                      }
                      setFonzipHandoff({ url: res.data.paymentPageUrl, ref_code: res.data.ref_code });
                    } else if (res.data.paymentPageUrl) {
                      if (win) win.close();
                      window.location.href = res.data.paymentPageUrl;
                    } else if (res.data.checkoutFormContent) {
                      // Fallback: render checkout form content
                      const w = win || window.open('', '_blank');
                      w.document.write(res.data.checkoutFormContent);
                    } else {
                      if (win) win.close();
                      toast.error(L("Ödeme sayfası oluşturulamadı"));
                    }
                  } catch (err) {
                    if (win) win.close();
                    const status = err.response?.status;
                    const detail = err.response?.data?.detail || L("Ödeme başlatılamadı");
                    // Kart kanalı yapılandırılmamış ya da geçici erişilemez → tek tıkla havaleye geç
                    if (status === 503 || status >= 500 || detail.includes("henüz yapılandırılmadı")) {
                      setCardUnavailable(true);
                      setPaymentMethod("bank_transfer");
                      setStep(4);
                      toast.error(L("Kart ile ödeme şu anda kullanılamıyor — banka havalesi/EFT ile devam edebilirsin."));
                    } else {
                      toast.error(detail);
                    }
                  } finally {
                    setSubmitting(false);
                  }
                }}
                disabled={submitting}
                data-testid="donation-iyzico-pay"
              >
                {submitting ? L("Yönlendiriliyor...") : (monthlyViaFonzip ? L("Fonzip ile Düzenli Öde →") : cardViaFonzip ? L("Fonzip ile Kartla Öde →") : L("Güvenli Ödeme Sayfasına Git"))}
              </Button>
              <Button variant="outline" className="btn-pill" onClick={() => setStep(skipPaymentStep ? 2 : 3)} data-testid="donation-iyzico-back">
                <ChevronLeft className="w-4 h-4 mr-2" /> {skipPaymentStep ? L("Geri") : L("Geri (Ödeme Yöntemi Değiştir)")}
              </Button>
            </div>
          )}

          {/* Step 5: Thank You */}
          {step === 5 && (
            <div className="space-y-5 text-center" data-testid="donation-thanks">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-heading text-2xl">{L(t("donation.thanks_title", "Teşekkürler!"))}</h2>
              <p className="text-muted-foreground">{getThankYouText()}</p>

              {paymentMethod === "bank_transfer" && (
                <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-xl">
                  {L("Ödemeniz kontrol edilince bağışınız onaylanacak.")}
                </p>
              )}

              <p className="text-sm text-muted-foreground">
                {L("Dilersen gelişmeleri takip etmek için WhatsApp üzerinden güncelleme alabilirsin.")}
              </p>

              {upsellVariant !== "none" && (
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
                {upsellVariant === "browse" ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {L("Desteğin için teşekkürler. Yuva bekleyen diğer canlara da göz atmak ister misin?")}
                    </p>
                    <Link to="/canlar" onClick={handleClose}>
                      <Button variant="outline" className="btn-pill mt-3 border-primary text-primary" data-testid="donation-upsell-browse">
                        <PawPrint className="w-4 h-4 mr-2" /> {L("Diğer Canları Gör")}
                      </Button>
                    </Link>
                  </>
                ) : upsellVariant === "meal" ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {dogName
                        ? L("Bir de mama desteği: {name} gibi canların günlük beslenmesi için en hızlı katkı.").replace("{name}", dogName)
                        : L("Bir de mama desteği: canların günlük beslenmesi için en hızlı katkı.")}
                    </p>
                    <Button
                      variant="outline"
                      className="btn-pill mt-3 border-primary text-primary"
                      onClick={() => {
                        if (onUpsellMeal) {
                          resetFlowState();
                          onUpsellMeal();
                        } else {
                          handleClose();
                        }
                      }}
                      data-testid="donation-upsell-meal"
                    >
                      <Utensils className="w-4 h-4 mr-2" /> {L("Mama Desteği de Ver")}
                    </Button>
                  </>
                ) : upsellVariant === "adopt" ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {dogName
                        ? L("Belki de asıl niyetin farklı: {name} ile aynı çatının altında yaşamak…").replace("{name}", dogName)
                        : L("Belki de asıl niyetin farklı: bir canı yüreğine alıp yuvana götürmek…")}
                    </p>
                    <Link to={dogId ? `/sahiplen/${dogId}` : "/canlar"} onClick={handleClose}>
                      <Button variant="outline" className="btn-pill mt-3 border-primary text-primary" data-testid="donation-upsell-adopt">
                        <Heart className="w-4 h-4 mr-2" /> {L("Sahiplenmeden Rahat Edemeyeceğim")}
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {L("Daha sürdürülebilir etki için:")} <strong>{L("Koruyucu Aile (Aylık Destek)")}</strong> {L("ile devam edebilirsin.")}
                    </p>
                    <Link to={dogId ? `/koruyucu-aile/${dogId}` : "/canlar"} onClick={handleClose}>
                      <Button variant="outline" className="btn-pill mt-3 border-primary text-primary" data-testid="donation-upsell-guardian">
                        <Heart className="w-4 h-4 mr-2" /> {dogId ? L("Koruyucu Aile Ol") : L("Kime Koruyucu Aile Olacağını Seç")}
                      </Button>
                    </Link>
                  </>
                )}
              </div>
              )}

              <Button onClick={handleClose} className="w-full btn-pill" variant="outline" data-testid="donation-close">
                {L("Kapat")}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
